import { pickImage, type ImageRef } from '../model/media.ts';
import { KEY_JUMP_SEC, type PlayerCommand } from './input.ts';

/**
 * §10 Phase 4's Media Session integration, as data.
 *
 * What the OS shows on a lock screen, in a notification, on a car head unit and under a hardware
 * media key. On web that is `navigator.mediaSession`; on Android TV it is a `MediaSessionService`,
 * which §10 Phase 5 lists as a *listing gate* (TV-NP) rather than a nicety. Both want the same four
 * things — what is playing, whether it is playing, where the playhead is, which buttons exist — so
 * all four are decided here and neither platform gets to decide them differently.
 *
 * ### Why a snapshot rather than a set of calls
 *
 * The obvious shape is an adapter with `setTitle`, `setPlaying`, `setPosition`. It is the wrong one,
 * for the same reason `PlayerPort.apply()` takes a whole `PlaybackPreferences` (R6): the platform has
 * to be able to answer *"what should the session hold right now"* from scratch, because both
 * platforms lose the session underneath us — the browser when another tab starts playing, Android
 * when the service is rebound — and a stream of deltas has no way to recover from that. A snapshot
 * is idempotent, so re-publishing is always correct and {@link mediaSessionDiff} is only an
 * optimisation.
 *
 * ### The three-way `status`, which is the one field that is not obvious
 *
 * `'playing' | 'paused'` is us stating what the transport reports. `'none'` is us stating that
 * nothing is loaded. **`null` is us declining to state anything**, and it exists because §7's no-MSE
 * arm is real: there is no `PlayerPort` there, only a plain `<video controls>` whose position the app
 * never sees (a known gap — see docs/specs/known-gaps.md). Publishing `'paused'` for it would freeze the OS controls on a lie
 * while the browser's own accounting — which is correct, because it is reading the element — gets
 * overwritten. So that arm publishes metadata and nothing else.
 */

// ---------------------------------------------------------------------------
// What the session holds
// ---------------------------------------------------------------------------

/**
 * The buttons a session can offer.
 *
 * Deliberately the *web* API's action names rather than a neutral vocabulary of our own. Media3's
 * command set is a superset (`COMMAND_SEEK_BACK`, `COMMAND_SEEK_TO`, …) so the Android adapter maps a
 * table to a table either way, and inventing a third naming scheme in between would only add a place
 * for the two to drift.
 *
 * Four of the API's actions are deliberately absent and each for its own measured reason:
 *
 * - **`stop`** — there is nothing to stop *to*. The page stays, the stream stays loaded, and the only
 *   thing it could do is pause, which `pause` already does. A second button that does what the first
 *   does is worse than no button.
 * - **`previoustrack` / `nexttrack`** — there is no queue. The watch route carries `list` and `index`
 *   (§8's URL scheme) but the Watch page never fetches the playlist's entries, so "next" is not a
 *   question this screen can answer without a request it does not make. Registering them would put
 *   two dead buttons on every lock screen. They are named here rather than forgotten because a later
 *   slice that adds playlist playback adds them to {@link MediaSessionSnapshot.actions} and nothing
 *   else changes.
 * - **`skipad`, `togglecamera`, `togglemicrophone`, `hangup`** — not this application.
 */
export type MediaSessionActionId = 'play' | 'pause' | 'seekbackward' | 'seekforward' | 'seekto';

/**
 * Every action this app can ever offer, in a fixed order.
 *
 * The order is load-bearing rather than cosmetic: {@link MediaSessionSnapshot.actions} is compared as
 * a sequence, and the platform iterates this list to decide which handlers to *clear* as well as
 * which to set. An action that is only ever added to a snapshot and never removed from the list of
 * candidates is an action that stays registered after it stops being available.
 */
export const MEDIA_SESSION_ACTIONS: readonly MediaSessionActionId[] = [
  'play',
  'pause',
  'seekbackward',
  'seekforward',
  'seekto',
];

/** Ours to state, or `null` for "not ours to state" — see the header. */
export type MediaSessionStatus = 'none' | 'paused' | 'playing';

/**
 * One artwork candidate. Numbers, not a `"320x180"` string (non-negotiable 5) — the platform formats
 * the API's `sizes` attribute, because only the platform has an API to format it *for*.
 */
export interface MediaSessionArtwork {
  /** Already rewritten to the instance (R2). A googlevideo or ytimg host here would be an R9 leak. */
  readonly url: string;
  readonly widthPx: number | null;
  readonly heightPx: number | null;
}

export interface MediaSessionMetadata {
  readonly title: string;
  /** The channel. `artist` because that is the slot the OS renders as the second line. */
  readonly artist: string;
  /**
   * Always `null` today, and typed rather than dropped.
   *
   * `album` is the *collection* a track belongs to, and a YouTube video belongs to none that this
   * screen knows: the route's `list` is an id, and the playlist's title would be another request. A
   * slice that adds playlist playback fills this in; until then, stating the site's own name here —
   * which is what most web players do with the field — would put "Visious" on a lock screen in the
   * slot reserved for what the reader is watching.
   */
  readonly album: string | null;
  /** Largest-useful first is *not* the rule here — see {@link mediaSessionArtwork}. */
  readonly artwork: readonly MediaSessionArtwork[];
}

/**
 * Where the playhead is, in the form the OS extrapolates from.
 *
 * All three fields are validated by {@link mediaSessionPositionOf} before one of these exists,
 * because the web API's setter *throws* on values a `<video>` produces constantly: `NaN` before
 * metadata loads, `Infinity` on a live stream, a position past the duration on the last frame. A
 * `TypeError` four times a second is what the validation is for.
 */
export interface MediaSessionPositionState {
  readonly durationSec: number;
  readonly positionSec: number;
  /** Never zero: the API treats `0` as invalid rather than as "paused" (paused is `status`). */
  readonly playbackRate: number;
}

export interface MediaSessionSnapshot {
  /** `null` before the video record has arrived, and on release. */
  readonly metadata: MediaSessionMetadata | null;
  readonly status: MediaSessionStatus | null;
  /** `null` where there is no duration to divide up: a live stream, or a load that has not landed. */
  readonly position: MediaSessionPositionState | null;
  readonly actions: readonly MediaSessionActionId[];
}

/** Nothing is playing and nothing is claimed. What a platform publishes on release. */
export const IDLE_MEDIA_SESSION: MediaSessionSnapshot = {
  metadata: null,
  status: 'none',
  position: null,
  actions: [],
};

// ---------------------------------------------------------------------------
// Building a snapshot
// ---------------------------------------------------------------------------

/**
 * The artwork ladder, in pixels of width.
 *
 * Two rungs rather than five, and they are two because of what this API actually offers.
 * `videoThumbnails[]` carries nine entries on every video in the corpus, but only three of them are
 * the video's own frame — `maxres` and `maxresdefault` at 1280×720 and `medium` at 320×180. The rest
 * (`sddefault` 640×480, `high` 480×360, `default`/`start`/`middle`/`end` 120×90) are 4:3 boxes with
 * black bars, which is why the pick asks for `widescreen`: an OS that crops artwork to a square would
 * be cropping the bars.
 *
 * So the two rungs resolve to exactly those two real images, and the reason to offer both rather than
 * one is cost. `medium` is **13.5 KB** and `maxres` is up to **266 KB** (measured in `pickImage`), the
 * browser fetches only the rung it picks, and which rung that is depends on a display we cannot see
 * from here — a desktop notification wants the small one and a phone lock screen wants the large one.
 * Offering one would either waste a quarter of a megabyte on a 128 px icon or hand a 4K television a
 * 320 px thumbnail.
 */
export const MEDIA_SESSION_ARTWORK_WIDTHS: readonly number[] = [128, 512];

/**
 * The artwork candidates for one image list, smallest first, deduplicated by URL.
 *
 * `pickImage` does the choosing rather than a filter written here, which keeps two measured facts in
 * one place: the arrays are sorted in opposite directions depending on the endpoint, and *smallest
 * sufficient* is the honest pick because a declared size is the size requested rather than the size
 * served — Invidious answers `/vi/{id}/maxres.jpg` with a 200 for every video, and `jNQXAC9IVRw`
 * declares 1280×720 while serving 480×360.
 *
 * That last fact is also why nothing here declares a **MIME type**, which the web API accepts and
 * would happily use: a declared `type` the browser disagrees with makes it *skip* the image, and an
 * array whose declared dimensions are already known to be occasionally wrong is not an array to add
 * another unverified claim to. Every platform in question sniffs the bytes.
 */
export function mediaSessionArtwork(images: readonly ImageRef[]): readonly MediaSessionArtwork[] {
  const artwork: MediaSessionArtwork[] = [];

  for (const minWidth of MEDIA_SESSION_ARTWORK_WIDTHS) {
    const image = pickImage(images, { minWidth, widescreen: true });
    if (image === null) continue;
    if (artwork.some((candidate) => candidate.url === image.url)) continue;
    artwork.push({ url: image.url, widthPx: image.width, heightPx: image.height });
  }

  return artwork;
}

/**
 * What the OS should say is playing.
 *
 * Takes the three fields it reads rather than a whole `VideoDetails`, so the same call works from a
 * playlist entry or a feed row if a later slice ever publishes one — and so a test can state the
 * input in four lines.
 */
export function mediaSessionMetadataFor(video: {
  readonly title: string;
  readonly author: string;
  readonly thumbnails: readonly ImageRef[];
}): MediaSessionMetadata {
  return {
    title: video.title,
    artist: video.author,
    album: null,
    artwork: mediaSessionArtwork(video.thumbnails),
  };
}

/** The numbers the app already polls for its own transport bar (§7). Nothing is sampled twice. */
export interface MediaSessionTransport {
  readonly positionSec: number;
  /** `null` or `0` for "no duration known" — a live stream, or a load that has not resolved. */
  readonly durationSec: number | null;
  readonly playbackRate: number;
  readonly playing: boolean;
}

/**
 * A position state, or `null` when there is nothing safe to publish.
 *
 * Every guard here corresponds to a value a real `<video>` produces, and to a `TypeError` the web
 * API throws for it:
 *
 * - **no duration, `0`, `NaN` or `Infinity`** → no position state at all. A live stream reports
 *   `Infinity` and the API's own duration field is a `double` that the OS turns into a scrub bar;
 *   there is no honest bar for an unbounded stream, so it gets none and the OS shows play/pause only.
 * - **a position past the duration** → clamped. The API rejects `position > duration` outright, and
 *   a `<video>` reports exactly that on the last frame of some streams.
 * - **`playbackRate: 0`** → `1`. The API rejects zero, because zero is what `status: 'paused'` is
 *   for; a rate of zero reaching here would take the whole position state down with it.
 */
export function mediaSessionPositionOf(
  transport: MediaSessionTransport,
): MediaSessionPositionState | null {
  const duration = transport.durationSec;
  if (duration === null || !Number.isFinite(duration) || duration <= 0) return null;

  const positionSec = Number.isFinite(transport.positionSec)
    ? Math.min(Math.max(0, transport.positionSec), duration)
    : 0;
  const rate = transport.playbackRate;
  const playbackRate = Number.isFinite(rate) && rate > 0 ? rate : 1;

  return { durationSec: duration, positionSec, playbackRate };
}

export interface MediaSessionInput {
  readonly metadata: MediaSessionMetadata | null;
  /**
   * `null` when the platform has no transport of its own to report — §7's no-MSE arm, where the
   * browser is reading the element and we are not. See the header on the three-way `status`.
   */
  readonly transport: MediaSessionTransport | null;
}

/**
 * Everything the session should hold, from what the screen already knows.
 *
 * The action set follows the *position* rather than the plan's `kind`, which is what makes live
 * correct without naming it: a stream with no duration has nothing to seek within, so it offers
 * play/pause and the OS draws no scrub bar. A DVR window has a real duration and gets the full set,
 * which is exactly the distinction §7 draws between the two.
 *
 * `play` and `pause` are both offered whenever anything is offered, and never conditioned on which
 * one is currently meaningful. The OS decides which button to *draw* from `status`; a handler set that
 * flipped with the play state would make the button vanish and reappear on every pause.
 */
export function mediaSessionSnapshot(input: MediaSessionInput): MediaSessionSnapshot {
  const { metadata, transport } = input;
  if (transport === null) return { metadata, status: null, position: null, actions: [] };

  const position = mediaSessionPositionOf(transport);

  return {
    metadata,
    status: transport.playing ? 'playing' : 'paused',
    position,
    actions:
      position === null
        ? ['play', 'pause']
        : ['play', 'pause', 'seekbackward', 'seekforward', 'seekto'],
  };
}

// ---------------------------------------------------------------------------
// Publishing: which fields actually changed
// ---------------------------------------------------------------------------

/**
 * How far the OS's own extrapolation may drift before it is worth correcting.
 *
 * The whole point of handing over a `playbackRate` is that the OS advances the position itself, so a
 * player that re-publishes the position on every sample is doing work the API exists to avoid — and on
 * a platform where each publish redraws a notification, doing it four times a second is visible. The
 * tolerance is what turns "publish constantly" into "publish when something happened": at 4 Hz a
 * playing video drifts by ~0.25 s per sample against an extrapolation that is tracking it, so nothing
 * is published, while any seek exceeds 1.5 s on the first sample after it.
 */
export const MEDIA_SESSION_POSITION_TOLERANCE_SEC = 1.5;

/** Which of the four fields the platform has to write. */
export interface MediaSessionUpdate {
  readonly metadata: boolean;
  readonly status: boolean;
  readonly actions: boolean;
  readonly position: boolean;
  /** The cheap early return: false means this sample costs the platform nothing at all. */
  readonly any: boolean;
}

export interface MediaSessionDiffInput {
  /**
   * What the platform's session currently holds — a *shadow*, updated field by field as each write
   * lands, not simply the last snapshot handed in. The difference matters: a sample that republished
   * the metadata and not the position must leave the position shadow (and its age) alone, or the next
   * drift check measures against a position the OS was never given.
   */
  readonly published: MediaSessionSnapshot | null;
  readonly next: MediaSessionSnapshot;
  /** Wall-clock seconds since `published.position` was handed over. */
  readonly sincePositionSec: number;
}

function sameArtwork(
  a: readonly MediaSessionArtwork[],
  b: readonly MediaSessionArtwork[],
): boolean {
  if (a.length !== b.length) return false;
  return a.every((image, index) => {
    const other = b[index];
    return (
      other !== undefined &&
      image.url === other.url &&
      image.widthPx === other.widthPx &&
      image.heightPx === other.heightPx
    );
  });
}

function sameMetadata(a: MediaSessionMetadata | null, b: MediaSessionMetadata | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.title === b.title &&
    a.artist === b.artist &&
    a.album === b.album &&
    sameArtwork(a.artwork, b.artwork)
  );
}

function sameActions(
  a: readonly MediaSessionActionId[],
  b: readonly MediaSessionActionId[],
): boolean {
  return a.length === b.length && a.every((action, index) => action === b[index]);
}

/**
 * What has to be written, given what the session already holds.
 *
 * Three of the four are ordinary equality. The position is the interesting one, and it has four
 * triggers rather than "the number changed":
 *
 * 1. **Nothing has been published yet** — including the case where a position appears for the first
 *    time because the duration finally arrived.
 * 2. **The duration or the rate changed.** Both are inputs to the OS's extrapolation, so a stale one
 *    is not a slightly-wrong position but a wrong *slope*.
 * 3. **The status changed.** Pausing has to hand over where playback actually stopped: the OS stops
 *    extrapolating when it hears `paused`, and whatever it had extrapolated to by then is what it
 *    keeps showing.
 * 4. **The observed position has left the extrapolation** by more than
 *    {@link MEDIA_SESSION_POSITION_TOLERANCE_SEC} — a seek, a chapter click, a recovery reload
 *    resuming at a stored position (R6), or a tab that was throttled in the background long enough
 *    for the two to separate.
 *
 * A `null` `status` writes nothing, ever: that is the "not ours to state" arm, and the platform must
 * leave the browser's own accounting where it found it. A `null` `position` where one *was* published
 * is a clear rather than a no-op — the duration going away means the scrub bar is now a claim nothing
 * supports.
 */
export function mediaSessionDiff(input: MediaSessionDiffInput): MediaSessionUpdate {
  const { published, next } = input;
  const previousPosition = published?.position ?? null;

  const metadata = !sameMetadata(published?.metadata ?? null, next.metadata);
  const status = next.status !== null && published?.status !== next.status;
  const actions = !sameActions(published?.actions ?? [], next.actions);

  const position = ((): boolean => {
    if (next.position === null) return previousPosition !== null;
    if (previousPosition === null) return true;
    if (previousPosition.durationSec !== next.position.durationSec) return true;
    if (previousPosition.playbackRate !== next.position.playbackRate) return true;
    if (status) return true;
    // Only a session the OS believes is *playing* advances on its own; a paused one is expected to
    // sit exactly where it was left, so any movement at all is a seek.
    const advancedSec =
      published?.status === 'playing' ? input.sincePositionSec * previousPosition.playbackRate : 0;
    const expectedSec = previousPosition.positionSec + advancedSec;
    return Math.abs(next.position.positionSec - expectedSec) > MEDIA_SESSION_POSITION_TOLERANCE_SEC;
  })();

  return { metadata, status, actions, position, any: metadata || status || actions || position };
}

/**
 * Fold a write that happened into the shadow — one field at a time, for the reason
 * {@link MediaSessionDiffInput.published} gives.
 *
 * Here rather than in the platform because getting it wrong is silent: a shadow updated wholesale
 * would record a position the OS never received, and the drift check would then measure against it
 * and conclude that nothing needs publishing for as long as the video plays.
 */
export function mediaSessionPublished(
  published: MediaSessionSnapshot | null,
  next: MediaSessionSnapshot,
  wrote: MediaSessionUpdate,
): MediaSessionSnapshot {
  const base = published ?? { metadata: null, status: null, position: null, actions: [] };
  return {
    metadata: wrote.metadata ? next.metadata : base.metadata,
    status: wrote.status ? next.status : base.status,
    position: wrote.position ? next.position : base.position,
    actions: wrote.actions ? next.actions : base.actions,
  };
}

// ---------------------------------------------------------------------------
// Incoming: an OS button becomes a player command
// ---------------------------------------------------------------------------

/**
 * What the platform's action event carried, if anything. Both fields are optional in the web API and
 * absent for most actions, which is why both are nullable here rather than defaulted at the boundary:
 * the *default* is a §7 number, and §7 lives in core.
 */
export interface MediaSessionActionDetail {
  /** `seekOffset` — how far the OS asked to skip. `null` when it did not name a distance. */
  readonly seekOffsetSec: number | null;
  /** `seekTime` — the absolute position a scrub landed on. */
  readonly seekTimeSec: number | null;
}

/** Nothing was carried. The shape most actions arrive with. */
export const NO_MEDIA_SESSION_DETAIL: MediaSessionActionDetail = {
  seekOffsetSec: null,
  seekTimeSec: null,
};

/**
 * An OS button as a {@link PlayerCommand} — the same vocabulary the keymap and the tap gesture
 * produce, so the screen keeps exactly one command handler (`playback/input.ts`).
 *
 * Two things here are decisions rather than plumbing:
 *
 * - **`play` and `pause` are not `toggle-play`.** The OS sends the action it thinks is needed, from a
 *   `status` it was told at some point in the past, so a `play` can arrive while playback is already
 *   running — a second lock-screen press racing the first, a hardware key on a stream that just
 *   recovered. Folding both onto a toggle turns that into a pause, which is the opposite of what was
 *   asked for. This is why the three arms exist in the vocabulary at all.
 * - **An unhonoured skip distance is §7's**, not zero and not the API's suggestion. `seekOffset` is
 *   optional and Chrome omits it; the fallback is {@link KEY_JUMP_SEC}, so the OS's skip buttons move
 *   by the same ten seconds `J`/`L` do and the app has one skip distance rather than two.
 *
 * `seekto` with no `seekTime` is refused rather than defaulted: there is no position that "seek to
 * unspecified" could honestly mean, and seeking to zero is the one guess that would throw away the
 * reader's place.
 */
export function mediaSessionCommand(
  action: MediaSessionActionId,
  detail: MediaSessionActionDetail = NO_MEDIA_SESSION_DETAIL,
): PlayerCommand | null {
  const offsetSec =
    detail.seekOffsetSec !== null &&
    Number.isFinite(detail.seekOffsetSec) &&
    detail.seekOffsetSec > 0
      ? detail.seekOffsetSec
      : KEY_JUMP_SEC;

  switch (action) {
    case 'play':
      return { kind: 'play' };
    case 'pause':
      return { kind: 'pause' };
    case 'seekbackward':
      return { kind: 'seek-by', seconds: -offsetSec };
    case 'seekforward':
      return { kind: 'seek-by', seconds: offsetSec };
    case 'seekto': {
      const seconds = detail.seekTimeSec;
      if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return null;
      return { kind: 'seek-to', seconds };
    }
  }
}
